import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FiArrowLeft, FiCalendar, FiDownload, FiEdit, FiEye, FiFileText, FiLink, FiPlay, FiPlus, FiRefreshCw, FiTrash2, FiUpload, FiVideo } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { companiesAPI, activitiesAPI, meetingsAPI, settingsAPI, usersAPI, zoomAPI, companyDocumentsAPI, jobOpeningsAPI } from '../../services/api';
import Modal from '../../components/Common/Modal';
import AuditTimeline from '../../components/Common/AuditTimeline';
import EmailThreadPanel from '../../components/Common/EmailThreadPanel';
import { useAuth } from '../../context/AuthContext';
import { getDocumentFileSizeError } from '../../utils/uploadLimits';

const COUNTRY_OPTIONS = ['INDIA', 'FRANCE'];
const PROSPECT_TEMPERATURE_OPTIONS = ['Cold', 'Warm', 'Hot'];
const PARTNER_STATUS_OPTIONS = ['Active', 'Former'];
const RELATIONSHIP_STAGE_OPTIONS = ['job_dating', 'ongoing_placement', 'active_search', 'priority_placement'];
const LEAD_STATUS_OPTIONS = ['Cold', 'Hot', 'Active', 'Old'];
const MEETING_STATUSES = ['Scheduled', 'Completed', 'Cancelled', 'Rescheduled'];
const JOB_TYPES = ['Internship', 'Alternance', 'Full-time', 'Part-time'];
const CONTRACT_TYPES = ['CDI', 'CDD', 'Alternance', 'Stage', 'Freelance', 'Other'];

const CompanyDetail = () => {
  const { t } = useTranslation();
  const { canPerformAction } = useAuth();
  const { id } = useParams();
  const navigate = useNavigate();
  const canUpdateCompanies = canPerformAction('companies', 'update');
  const canDeleteCompanies = canPerformAction('companies', 'delete');
  const canCreateJobs = canPerformAction('jobs', 'create');
  const canUpdateJobs = canPerformAction('jobs', 'update');
  const canDeleteJobs = canPerformAction('jobs', 'delete');
  const [company, setCompany] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [activities, setActivities] = useState([]);
  const [meetings, setMeetings] = useState([]);
  const [jobOpenings, setJobOpenings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [programTypes, setProgramTypes] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('details');
  const [showEditModal, setShowEditModal] = useState(false);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [showMeetingModal, setShowMeetingModal] = useState(false);
  const [showJobModal, setShowJobModal] = useState(false);
  const [showUploadDocumentModal, setShowUploadDocumentModal] = useState(false);
  const [uploadingDocuments, setUploadingDocuments] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [showRescheduleModal, setShowRescheduleModal] = useState(false);
  const [zoomConfigured, setZoomConfigured] = useState(false);
  const [zoomGeneratingModal, setZoomGeneratingModal] = useState(false);
  const [zoomGenerating, setZoomGenerating] = useState(null);
  const [zoomSyncing, setZoomSyncing] = useState(null);
  const [zoomGeneratingReschedule, setZoomGeneratingReschedule] = useState(false);
  const [editingMeeting, setEditingMeeting] = useState(null);
  const [rescheduleMeeting, setRescheduleMeeting] = useState(null);
  const [rescheduleForm, setRescheduleForm] = useState(null);
  const [updatingMeetingStatusId, setUpdatingMeetingStatusId] = useState(null);
  const [editForm, setEditForm] = useState({
    country_code: 'FRANCE',
    company_name: '',
    siret_number: '',
    company_address: '',
    company_phone: '',
    company_email: '',
    contact_first_name: '',
    contact_last_name: '',
    contact_position: '',
    contact_phone: '',
    contact_email: '',
    relationship_manager_id: '',
    prospect_temperature: 'Cold',
    partner_status: 'Active',
    has_open_position: false,
    relationship_stage: '',
    phone: '',
    email: '',
    assigned_employee_id: '',
    lead_status: '',
    notes: ''
  });
  const [activityForm, setActivityForm] = useState({
    activity_type: 'Call',
    activity_date: new Date().toISOString().slice(0, 10),
    activity_time: '',
    next_followup: '',
    notes: ''
  });
  const [meetingForm, setMeetingForm] = useState({
    title: '',
    meeting_type: 'Company Meeting',
    meeting_date: '',
    meeting_time: '',
    timezone: 'Asia/Colombo',
    mode: 'Online',
    location: '',
    meeting_link: '',
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_passcode: '',
    notes: '',
    status: 'Scheduled'
  });
  const [documentForm, setDocumentForm] = useState({
    document_type: 'General',
    notes: '',
    files: []
  });
  const [jobForm, setJobForm] = useState({
    offer_title: '',
    program_type: 'All',
    job_type: 'Internship',
    contract_type: 'Alternance',
    responsibilities: '',
    exchange_history: '',
    last_contact_date: '',
    followup_action: '',
    email_stage: '',
    assigned_manager_id: '',
    handover_manager_id: '',
    meeting_history: '',
    next_followup_date: '',
    reminder_enabled: false
  });
  const emptyMeetingForm = useCallback(() => ({
    title: '',
    meeting_type: 'Company Meeting',
    meeting_date: '',
    meeting_time: '',
    timezone: 'Asia/Colombo',
    mode: 'Online',
    location: '',
    meeting_link: '',
    zoom_meeting_id: '',
    zoom_join_url: '',
    zoom_start_url: '',
    zoom_passcode: '',
    notes: '',
    status: 'Scheduled'
  }), []);

  const tabs = useMemo(() => ([
    { key: 'details', label: t('common.details', 'Details') },
    { key: 'activities', label: t('studentDetail.tabs.activities', 'Activities') },
    { key: 'meetings', label: t('studentDetail.tabs.meetings', 'Meetings') },
    { key: 'job-openings', label: t('jobs.title', 'Job Openings') },
    { key: 'documents', label: t('studentDetail.tabs.documents', 'Documents') },
    { key: 'emails', label: t('studentDetail.tabs.emails', 'Emails') },
    { key: 'history', label: t('studentDetail.tabs.history', 'History') }
  ]), [t]);

  const matchesCompanyId = useCallback((record) => {
    const companyId = Number(id);
    if (!record || !companyId) return false;
    const candidates = [
      record.company_id,
      record.entity_id,
      record.reference_id,
      record.related_company_id
    ];
    return candidates.some((value) => Number(value) === companyId);
  }, [id]);

  const isCompanyHistoryRecord = useCallback((record) => {
    if (!record) return false;
    const entityType = String(record.entity_type || record.module || '').toLowerCase();
    const relatedEntityType = String(record.related_entity_type || '').toLowerCase();
    const hasCompanyType = entityType.includes('company') || relatedEntityType.includes('company');
    return matchesCompanyId(record) || hasCompanyType;
  }, [matchesCompanyId]);

  const loadCompany = useCallback(async () => {
    setLoading(true);
    try {
      const companyRes = await companiesAPI.getById(id);
      setCompany(companyRes.data || null);
    } catch (err) {
      toast.error(err?.response?.data?.message || t('companies.failedLoad'));
      navigate('/companies');
      return;
    }

    try {
      const [actRes, meetRes, histRes, docRes, jobRes] = await Promise.allSettled([
        activitiesAPI.getAll({ company_id: id, limit: 200 }),
        meetingsAPI.getAll({ company_id: id, limit: 200, page: 1 }),
        settingsAPI.getAuditLog({ company_id: id, include_related: 1, limit: 200 }),
        companyDocumentsAPI.getByCompany(id),
        jobOpeningsAPI.getAll({ company_id: id, limit: 200, page: 1 })
      ]);

      const rawActivities = actRes.status === 'fulfilled' ? (actRes.value?.data?.activities || []) : [];
      const rawMeetings = meetRes.status === 'fulfilled' ? (meetRes.value?.data?.meetings || []) : [];
      const rawHistory = histRes.status === 'fulfilled' ? (histRes.value?.data || []) : [];
      const rawDocuments = docRes.status === 'fulfilled' ? (docRes.value?.data?.documents || []) : [];
      const rawJobs = jobRes.status === 'fulfilled' ? (jobRes.value?.data?.jobs || []) : [];

      // Safety filter: only show this company's records even if backend returns wider results.
      setActivities(rawActivities.filter(matchesCompanyId));
      setMeetings(rawMeetings.filter(matchesCompanyId));
      setHistory(rawHistory.filter(isCompanyHistoryRecord));
      setDocuments(rawDocuments);
      setJobOpenings(rawJobs.filter((j) => Number(j?.company_id) === Number(id)));
    } finally {
      setLoading(false);
    }
  }, [id, navigate, t, matchesCompanyId, isCompanyHistoryRecord]);

  useEffect(() => {
    loadCompany();
  }, [loadCompany]);

  useEffect(() => {
    usersAPI.getSalesTeam().then((res) => setEmployees(res.data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    settingsAPI.getProgramTypes()
      .then((res) => {
        const rows = Array.isArray(res?.data) ? res.data : [];
        const franceRows = rows.filter((p) => {
          const cc = String(p?.country_code || '').toUpperCase();
          const status = String(p?.status || 'active').toLowerCase();
          return status === 'active' && (!cc || cc === 'FRANCE');
        });
        setProgramTypes(franceRows);
      })
      .catch(() => setProgramTypes([]));
  }, []);

  useEffect(() => {
    zoomAPI.getStatus().then((r) => setZoomConfigured(!!r?.data?.configured)).catch(() => setZoomConfigured(false));
  }, []);

  useEffect(() => {
    if (!company) return;
    setEditForm({
      country_code: company.country_code || 'FRANCE',
      company_name: company.company_name || '',
      siret_number: company.siret_number || '',
      company_address: company.company_address || '',
      company_phone: company.company_phone || '',
      company_email: company.company_email || '',
      contact_first_name: company.contact_first_name || '',
      contact_last_name: company.contact_last_name || '',
      contact_position: company.contact_position || '',
      contact_phone: company.contact_phone || '',
      contact_email: company.contact_email || '',
      relationship_manager_id: company.relationship_manager_id || '',
      prospect_temperature: company.prospect_temperature || 'Cold',
      partner_status: company.partner_status || 'Active',
      has_open_position: Number(company.has_open_position) === 1,
      relationship_stage: company.relationship_stage || '',
      phone: company.phone || '',
      email: company.email || '',
      assigned_employee_id: company.assigned_employee_id || '',
      lead_status: company.lead_status || '',
      notes: company.notes || ''
    });
  }, [company]);

  const handleCreateActivity = async () => {
    if (!activityForm.activity_type || !activityForm.activity_date || !activityForm.notes.trim()) {
      toast.error(t('leadDetail.toasts.fillActivityFields'));
      return;
    }
    try {
      await activitiesAPI.create({ ...activityForm, company_id: Number(id) });
      toast.success(t('leadDetail.toasts.activityLogged'));
      setShowActivityModal(false);
      setActivityForm({
        activity_type: 'Call',
        activity_date: new Date().toISOString().slice(0, 10),
        activity_time: '',
        next_followup: '',
        notes: ''
      });
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleCreateMeeting = async () => {
    if (!meetingForm.title.trim() || !meetingForm.meeting_date || !meetingForm.meeting_time) {
      toast.error(t('leadDetail.toasts.fillMeetingFields'));
      return;
    }
    if (meetingForm.mode === 'Offline' && !meetingForm.location.trim()) {
      toast.error(t('leadDetail.toasts.locationRequired'));
      return;
    }
    try {
      if (editingMeeting?.meeting_id) {
        await meetingsAPI.update(editingMeeting.meeting_id, {
          ...meetingForm,
          company_id: Number(id)
        });
        toast.success(t('leadDetail.toasts.meetingUpdated', 'Meeting updated'));
      } else {
        await meetingsAPI.create({ ...meetingForm, company_id: Number(id) });
        toast.success(t('leadDetail.toasts.meetingCreated', 'Meeting created'));
      }
      setShowMeetingModal(false);
      setEditingMeeting(null);
      setMeetingForm(emptyMeetingForm());
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };
  const formatTimeForInput = (value) => {
    if (!value) return '';
    const s = String(value);
    return s.length >= 5 ? s.slice(0, 5) : s;
  };

  const handleEditMeeting = (meeting) => {
    setEditingMeeting(meeting);
    setMeetingForm({
      title: meeting.title || '',
      meeting_type: meeting.meeting_type || 'Company Meeting',
      meeting_date: meeting.meeting_date ? String(meeting.meeting_date).slice(0, 10) : '',
      meeting_time: formatTimeForInput(meeting.meeting_time),
      timezone: meeting.timezone || 'Asia/Colombo',
      mode: meeting.mode || 'Online',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || '',
      notes: meeting.notes || '',
      status: meeting.status || 'Scheduled'
    });
    setShowMeetingModal(true);
  };

  const openRescheduleModal = (meeting) => {
    setRescheduleMeeting(meeting);
    setRescheduleForm({
      meeting_date: meeting.meeting_date ? String(meeting.meeting_date).slice(0, 10) : '',
      meeting_time: formatTimeForInput(meeting.meeting_time),
      timezone: meeting.timezone || 'Asia/Colombo',
      mode: meeting.mode || 'Online',
      location: meeting.location || '',
      meeting_link: meeting.meeting_link || '',
      zoom_meeting_id: meeting.zoom_meeting_id || '',
      zoom_join_url: meeting.zoom_join_url || '',
      zoom_start_url: meeting.zoom_start_url || '',
      zoom_passcode: meeting.zoom_passcode || ''
    });
    setShowRescheduleModal(true);
  };
  const closeRescheduleModal = () => {
    setShowRescheduleModal(false);
    setRescheduleMeeting(null);
    setRescheduleForm(null);
  };

  const handleMeetingStatusSelect = (meeting, nextStatus) => {
    if (nextStatus === 'Rescheduled') {
      openRescheduleModal(meeting);
      return;
    }
    (async () => {
      try {
        setUpdatingMeetingStatusId(meeting.meeting_id);
        await meetingsAPI.update(meeting.meeting_id, { status: nextStatus, company_id: Number(id) });
        toast.success(t('leadDetail.toasts.meetingUpdated', 'Meeting updated'));
        loadCompany();
      } catch (err) {
        toast.error(err?.response?.data?.message || t('leadDetail.toasts.statusUpdateFailed', 'Failed to update status'));
      } finally {
        setUpdatingMeetingStatusId(null);
      }
    })();
  };

  const handleRescheduleSubmit = async () => {
    if (!rescheduleMeeting || !rescheduleForm) return;
    if (!rescheduleForm.meeting_date || !rescheduleForm.meeting_time) {
      toast.error(t('leadDetail.toasts.fillMeetingFields', 'Please fill required meeting fields'));
      return;
    }
    if (rescheduleForm.mode === 'Offline' && !rescheduleForm.location.trim()) {
      toast.error(t('leadDetail.toasts.locationRequired', 'Location is required for offline meeting'));
      return;
    }
    try {
      setUpdatingMeetingStatusId(rescheduleMeeting.meeting_id);
      await meetingsAPI.update(rescheduleMeeting.meeting_id, {
        title: rescheduleMeeting.title,
        meeting_type: rescheduleMeeting.meeting_type,
        meeting_date: rescheduleForm.meeting_date,
        meeting_time: rescheduleForm.meeting_time,
        timezone: rescheduleForm.timezone || 'Asia/Colombo',
        mode: rescheduleForm.mode || 'Online',
        location: rescheduleForm.mode === 'Offline' ? (rescheduleForm.location || null) : null,
        meeting_link: rescheduleForm.mode === 'Online' ? (rescheduleForm.meeting_link || null) : null,
        notes: rescheduleMeeting.notes || null,
        status: 'Rescheduled',
        company_id: Number(id),
        zoom_meeting_id: rescheduleForm.zoom_meeting_id || null,
        zoom_join_url: rescheduleForm.zoom_join_url || null,
        zoom_start_url: rescheduleForm.zoom_start_url || null,
        zoom_passcode: rescheduleForm.zoom_passcode || null
      });
      toast.success(t('leadDetail.toasts.rescheduled', 'Meeting rescheduled'));
      closeRescheduleModal();
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('leadDetail.toasts.rescheduleFailed', 'Failed to reschedule'));
    } finally {
      setUpdatingMeetingStatusId(null);
    }
  };

  const handleGenerateZoomForMeetingModal = async () => {
    if (!meetingForm.meeting_date || !meetingForm.meeting_time) {
      toast.error(t('leadDetail.toasts.setDateTimeFirst', 'Please set date and time first'));
      return;
    }
    if (!meetingForm.title?.trim()) {
      toast.error(t('leadDetail.toasts.enterTitleFirst', 'Please enter title first'));
      return;
    }
    if (meetingForm.mode !== 'Online') {
      toast.error(t('leadDetail.toasts.switchOnline', 'Switch mode to Online to generate Zoom'));
      return;
    }
    setZoomGeneratingModal(true);
    try {
      const startTime = `${meetingForm.meeting_date.slice(0, 10)}T${meetingForm.meeting_time}:00`;
      const payload = {
        topic: meetingForm.title.trim() || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: meetingForm.timezone || 'Asia/Colombo',
        entity_type: 'meeting'
      };
      if (editingMeeting?.meeting_id) {
        payload.entity_id = editingMeeting.meeting_id;
      }
      const { data } = await zoomAPI.generate(payload);
      setMeetingForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('leadDetail.toasts.zoomCreated', 'Zoom link generated'));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('leadDetail.toasts.zoomFailed', 'Failed to generate Zoom link'));
    }
    setZoomGeneratingModal(false);
  };
  const generateZoomOnMeetingCard = async (meeting) => {
    setZoomGenerating(meeting.meeting_id);
    try {
      const startTime = `${String(meeting.meeting_date).slice(0, 10)}T${formatTimeForInput(meeting.meeting_time)}:00`;
      await zoomAPI.generate({
        topic: meeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: meeting.timezone || 'Asia/Colombo',
        entity_type: 'meeting',
        entity_id: meeting.meeting_id
      });
      toast.success(t('leadDetail.toasts.zoomCreated', 'Zoom link generated'));
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('leadDetail.toasts.zoomFailed', 'Failed to generate Zoom link'));
    }
    setZoomGenerating(null);
  };

  const syncZoomOnMeeting = async (meeting) => {
    if (!meeting.zoom_meeting_id) return;
    setZoomSyncing(meeting.meeting_id);
    try {
      await zoomAPI.sync({ entity_type: 'meeting', entity_id: meeting.meeting_id });
      toast.success(t('leadDetail.toasts.zoomSynced', 'Zoom synced'));
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('leadDetail.toasts.syncFailed', 'Sync failed'));
    }
    setZoomSyncing(null);
  };

  const generateZoomInRescheduleModal = async () => {
    if (!rescheduleMeeting || !rescheduleForm) return;
    if (!rescheduleForm.meeting_date || !rescheduleForm.meeting_time) {
      toast.error(t('leadDetail.toasts.setDateTimeFirst', 'Please set date and time first'));
      return;
    }
    setZoomGeneratingReschedule(true);
    try {
      const startTime = `${rescheduleForm.meeting_date.slice(0, 10)}T${rescheduleForm.meeting_time}:00`;
      const { data } = await zoomAPI.generate({
        topic: rescheduleMeeting.title || 'CRM Meeting',
        start_time: startTime,
        duration: 60,
        timezone: rescheduleForm.timezone || 'Asia/Colombo',
        entity_type: 'meeting',
        entity_id: rescheduleMeeting.meeting_id
      });
      setRescheduleForm((f) => ({
        ...f,
        meeting_link: data.join_url || f.meeting_link,
        zoom_meeting_id: data.zoom_meeting_id || '',
        zoom_join_url: data.join_url || '',
        zoom_start_url: data.start_url || '',
        zoom_passcode: data.passcode || ''
      }));
      toast.success(t('leadDetail.toasts.zoomCreated', 'Zoom link generated'));
    } catch (err) {
      toast.error(err?.response?.data?.message || t('leadDetail.toasts.zoomFailed', 'Failed to generate Zoom link'));
    }
    setZoomGeneratingReschedule(false);
  };

  const formatDateTime = (value) => {
    if (!value) return '-';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-IN', { timeZone: 'Asia/Kolkata' });
  };

  const relationshipStageLabel = (s) => {
    const key = {
      job_dating: 'companies.relationshipStages.jobDating',
      ongoing_placement: 'companies.relationshipStages.ongoingPlacement',
      active_search: 'companies.relationshipStages.activeSearch',
      priority_placement: 'companies.relationshipStages.priorityPlacement'
    }[s];
    return key ? t(key) : (s || '-');
  };

  const prospectTemperatureLabel = (s) => {
    const key = { Cold: 'companies.statuses.cold', Warm: 'companies.statuses.warm', Hot: 'companies.statuses.hot' }[s];
    return key ? t(key) : (s || '-');
  };
  const partnerStatusLabel = (s) => {
    const key = { Active: 'companies.partnerStatuses.active', Former: 'companies.partnerStatuses.former' }[s];
    return key ? t(key) : (s || '-');
  };

  const handleUpdateCompany = async () => {
    if (!editForm.company_name.trim()) {
      toast.error(t('companies.nameRequired'));
      return;
    }
    try {
      await companiesAPI.update(id, {
        ...editForm,
        relationship_manager_id: editForm.relationship_manager_id || null,
        assigned_employee_id: editForm.assigned_employee_id || null,
        has_open_position: !!editForm.has_open_position
      });
      toast.success(t('companies.updated'));
      setShowEditModal(false);
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleDeleteCompany = async () => {
    if (!window.confirm(t('companies.deleteConfirm'))) return;
    try {
      await companiesAPI.delete(id);
      toast.success(t('companies.deleted'));
      navigate('/companies');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleUploadCompanyDocuments = async () => {
    if (!documentForm.files.length) {
      toast.error(t('studentDetail.documents.selectFile', 'Please select file'));
      return;
    }
    for (const f of documentForm.files) {
      const err = getDocumentFileSizeError(f);
      if (err) {
        toast.error(err);
        return;
      }
    }
    const formData = new FormData();
    formData.append('document_type', documentForm.document_type || 'General');
    formData.append('notes', documentForm.notes || '');
    documentForm.files.forEach((f) => formData.append('files', f));
    try {
      setUploadingDocuments(true);
      await companyDocumentsAPI.upload(id, formData);
      toast.success(t('studentDetail.documents.uploadSuccess', 'Document uploaded successfully'));
      setShowUploadDocumentModal(false);
      setDocumentForm({ document_type: 'General', notes: '', files: [] });
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('studentDetail.documents.uploadFailed', 'Upload failed'));
    } finally {
      setUploadingDocuments(false);
    }
  };

  const handleDeleteCompanyDocument = async (docId) => {
    if (!window.confirm(t('studentDetail.documents.deleteConfirm', 'Delete this document?'))) return;
    try {
      await companyDocumentsAPI.delete(docId);
      toast.success(t('studentDetail.documents.deleted', 'Document deleted'));
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const openCreateJobModal = () => {
    setEditingJob(null);
    setJobForm({
      offer_title: '',
      program_type: 'All',
      job_type: 'Internship',
      contract_type: 'Alternance',
      number_of_positions: 1,
      responsibilities: '',
      exchange_history: '',
      last_contact_date: '',
      followup_action: '',
      email_stage: '',
      assigned_manager_id: '',
      handover_manager_id: '',
      meeting_history: '',
      next_followup_date: '',
      reminder_enabled: false
    });
    setShowJobModal(true);
  };

  const openEditJobModal = (job) => {
    setEditingJob(job);
    setJobForm({
      offer_title: job.offer_title || job.job_title || '',
      program_type: job.program_type || 'All',
      job_type: job.job_type || 'Internship',
      contract_type: job.contract_type || 'Alternance',
      number_of_positions: job.number_of_positions ?? 1,
      responsibilities: job.responsibilities || '',
      exchange_history: job.exchange_history || '',
      last_contact_date: job.last_contact_date ? String(job.last_contact_date).slice(0, 10) : '',
      followup_action: job.followup_action || '',
      email_stage: job.email_stage || '',
      assigned_manager_id: job.assigned_manager_id || '',
      handover_manager_id: job.handover_manager_id || '',
      meeting_history: job.meeting_history || '',
      next_followup_date: job.next_followup_date ? String(job.next_followup_date).slice(0, 10) : '',
      reminder_enabled: Number(job.reminder_enabled) === 1
    });
    setShowJobModal(true);
  };

  const handleSaveJobOpening = async () => {
    if (!jobForm.offer_title.trim()) {
      toast.error(t('jobs.titleRequired', 'Offer title is required'));
      return;
    }
    const payload = {
      ...jobForm,
      company_id: Number(id),
      assigned_manager_id: jobForm.assigned_manager_id || null,
      handover_manager_id: jobForm.handover_manager_id || null,
      number_of_positions: Math.max(1, Number(jobForm.number_of_positions) || 1),
      last_contact_date: jobForm.last_contact_date || null,
      next_followup_date: jobForm.next_followup_date || null
    };
    try {
      if (editingJob) {
        await jobOpeningsAPI.update(editingJob.job_id, payload);
        toast.success(t('jobs.updated', 'Job opening updated'));
      } else {
        await jobOpeningsAPI.create(payload);
        toast.success(t('jobs.created', 'Job opening created'));
      }
      setShowJobModal(false);
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleDeleteJobOpening = async (jobId) => {
    if (!window.confirm(t('jobs.deleteConfirm', 'Delete this job opening?'))) return;
    try {
      await jobOpeningsAPI.delete(jobId);
      toast.success(t('jobs.deleted', 'Job opening deleted'));
      loadCompany();
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  const handleViewCompanyDocument = async (docId) => {
    try {
      const res = await companyDocumentsAPI.getUrl(docId);
      const url = res?.data?.url;
      if (!url) {
        toast.error(t('common.failed'));
        return;
      }
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err?.response?.data?.message || t('common.failed'));
    }
  };

  if (loading) {
    return <div className="loading-spinner"><div className="spinner" /></div>;
  }

  if (!company) return null;

  return (
    <div>
      <div className="detail-header">
        <div className="detail-left">
          <h2>{company.company_name || '-'}</h2>
          <div className="detail-meta">
            <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
              {t('companies.siret')}: {company.siret_number || '-'}
            </span>
          </div>
        </div>
        <div className="detail-right" style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {canUpdateCompanies && (
            <button type="button" className="btn btn-sm btn-outline" onClick={() => setShowEditModal(true)}>
              <FiEdit /> {t('common.edit')}
            </button>
          )}
          {canDeleteCompanies && (
            <button type="button" className="btn btn-sm btn-danger" onClick={handleDeleteCompany}>
              <FiTrash2 /> {t('common.delete')}
            </button>
          )}
          <button type="button" className="btn btn-sm btn-outline" onClick={() => navigate('/companies')}>
            <FiArrowLeft /> {t('common.back', 'Back')}
          </button>
        </div>
      </div>

      <div className="tabs">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            className={activeTab === tab.key ? 'active' : ''}
            onClick={() => setActiveTab(tab.key)}
            type="button"
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'details' && (
        <div className="card">
          <div className="detail-info-grid">
            <div className="detail-info-item">
              <label>{t('common.countryCode', 'Country Code')}</label>
              <span>{company.country_code || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.companyName')}</label>
              <span>{company.company_name || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.siret')}</label>
              <span>{company.siret_number || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.companyPhone')}</label>
              <span>{company.company_phone || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.companyEmail')}</label>
              <span>{company.company_email || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.phone', 'Phone')}</label>
              <span>{company.phone || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.email', 'Email')}</label>
              <span>{company.email || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.contactFirstName')}</label>
              <span>{company.contact_first_name || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.contactLastName')}</label>
              <span>{company.contact_last_name || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.contactPosition')}</label>
              <span>{company.contact_position || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.contactPhone')}</label>
              <span>{company.contact_phone || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.contactEmail')}</label>
              <span>{company.contact_email || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.relationshipManagerName')}</label>
              <span>{company.relationship_manager_name || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.assignedEmployee', 'Assigned Employee')}</label>
              <span>{company.assigned_employee_name || company.assigned_employee_id || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.leadStatus', 'Lead Status')}</label>
              <span>{company.lead_status || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.prospectTemperature')}</label>
              <span>{company.prospect_temperature || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.partnerStatus')}</label>
              <span>{company.partner_status || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.hasOpenPosition')}</label>
              <span>{Number(company.has_open_position) === 1 ? t('common.yes') : t('common.no')}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('companies.relationshipStage')}</label>
              <span>{relationshipStageLabel(company.relationship_stage)}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.createdBy', 'Created By')}</label>
              <span>{company.created_by_name || company.created_by || '-'}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.createdAt', 'Created At')}</label>
              <span>{formatDateTime(company.created_at)}</span>
            </div>
            <div className="detail-info-item">
              <label>{t('common.updatedAt', 'Updated At')}</label>
              <span>{formatDateTime(company.updated_at)}</span>
            </div>
            <div className="detail-info-item" style={{ gridColumn: '1 / -1' }}>
              <label>{t('companies.companyAddress')}</label>
              <span>{company.company_address || '-'}</span>
            </div>
            <div className="detail-info-item" style={{ gridColumn: '1 / -1' }}>
              <label>{t('common.notes', 'Notes')}</label>
              <span>{company.notes || '-'}</span>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'activities' && (
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>{t('studentDetail.tabs.activities', 'Activities')}</h3>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowActivityModal(true)}>
              <FiPlus /> {t('leadDetail.logActivity', 'Log Activity')}
            </button>
          </div>
          {activities.length === 0 ? (
            <div className="empty-state"><p>{t('common.noData', 'No data found')}</p></div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>{t('activities.type', 'Type')}</th>
                    <th>{t('activities.date', 'Date')}</th>
                    <th>{t('activities.time', 'Time')}</th>
                    <th>{t('activities.notes', 'Notes')}</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a) => (
                    <tr key={a.activity_id}>
                      <td>{a.activity_type || '-'}</td>
                      <td>{a.activity_date || '-'}</td>
                      <td>{a.activity_time || '-'}</td>
                      <td>{a.notes || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>{t('studentDetail.historyTitles.activities', 'Activities History')}</summary>
              <div style={{ marginTop: 8 }}>
                <AuditTimeline logs={history.filter((h) => String(h?.entity_type || '').toLowerCase() === 'activity')} showEntity={false} maxCollapsedChanges={3} />
              </div>
            </details>
          </div>
        </div>
      )}

      {activeTab === 'meetings' && (
        <div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>{t('studentDetail.tabs.meetings', 'Meetings')}</h3>
            <button
              type="button"
              className="btn btn-primary btn-sm"
              onClick={() => {
                setEditingMeeting(null);
                setMeetingForm(emptyMeetingForm());
                setShowMeetingModal(true);
              }}
            >
              <FiCalendar /> {t('leadDetail.scheduleMeeting', 'Schedule Meeting')}
            </button>
          </div>
          {meetings.length === 0 ? (
            <div className="empty-state"><p>{t('studentDetail.meetings.noMeetings', 'No meetings found')}</p></div>
          ) : (
            <div>
              {meetings.map((m) => (
                <div className="card" key={m.meeting_id} style={{ marginBottom: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12 }}>
                    <div style={{ minWidth: 0 }}>
                      <h3 style={{ fontSize: 16, marginBottom: 4, fontWeight: 700 }}>{m.title}</h3>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 8 }}>
                        <span className="badge badge-new">{m.meeting_type}</span>
                        <span className="badge badge-followup">{m.mode}</span>
                        <select
                          value={m.status || 'Scheduled'}
                          onChange={(e) => handleMeetingStatusSelect(m, e.target.value)}
                          disabled={updatingMeetingStatusId === m.meeting_id}
                          style={{ minWidth: 130, height: 28, fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid var(--input-border)', padding: '0 8px' }}
                        >
                          {MEETING_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </div>
                      <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                        {m.meeting_date ? new Date(m.meeting_date).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'} at {formatTimeForInput(m.meeting_time)}
                        {m.timezone && ` (${m.timezone})`}
                        {m.location && ` | ${m.location}`}
                      </p>
                      {m.mode === 'Online' && m.meeting_link && (
                        <p style={{ fontSize: 13, marginTop: 6 }}>
                          <a href={m.meeting_link} target="_blank" rel="noreferrer" style={{ color: 'var(--primary)', fontWeight: 600 }}>{t('leadDetail.joinMeeting', 'Join')}</a>
                          {m.zoom_meeting_id && <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--text-muted)' }}>Zoom ID: {m.zoom_meeting_id}</span>}
                        </p>
                      )}
                      {m.mode === 'Online' && m.zoom_meeting_id && (
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', marginTop: 6 }}>
                          {m.zoom_recording_url && (
                            <a href={m.zoom_recording_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#059669', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <FiPlay size={12} /> Recording
                            </a>
                          )}
                          {m.zoom_transcript_url && (
                            <a href={m.zoom_transcript_url} target="_blank" rel="noreferrer" style={{ fontSize: 12, color: '#7c3aed', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                              <FiFileText size={12} /> Transcript
                            </a>
                          )}
                          {zoomConfigured && (
                            <button
                              type="button"
                              title="Sync Zoom recordings"
                              className="btn btn-sm btn-outline"
                              style={{ padding: '2px 8px', fontSize: 11, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                              disabled={zoomSyncing === m.meeting_id}
                              onClick={() => syncZoomOnMeeting(m)}
                            >
                              <FiRefreshCw size={12} className={zoomSyncing === m.meeting_id ? 'spin' : ''} /> Sync
                            </button>
                          )}
                        </div>
                      )}
                      {m.mode === 'Online' && !m.zoom_meeting_id && zoomConfigured && (
                        <div style={{ marginTop: 8 }}>
                          <button
                            type="button"
                            className="btn btn-sm btn-outline"
                            style={{ fontSize: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}
                            disabled={zoomGenerating === m.meeting_id}
                            onClick={() => generateZoomOnMeetingCard(m)}
                          >
                            <FiVideo size={12} /> {zoomGenerating === m.meeting_id ? t('leadDetail.creatingZoom', 'Creating Zoom...') : t('leadDetail.generateZoom', 'Generate Zoom')}
                          </button>
                        </div>
                      )}
                      {m.notes && <p style={{ marginTop: 4, fontSize: 13, color: 'var(--text-secondary)' }}>{m.notes}</p>}
                      {m.outcome && <p style={{ marginTop: 4, fontWeight: 600, color: 'var(--success)' }}>Outcome: {m.outcome}</p>}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      <button className="btn btn-sm btn-outline" onClick={() => handleEditMeeting(m)}>
                        <FiEdit /> {t('common.edit')}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>{t('studentDetail.historyTitles.meetings', 'Meetings History')}</summary>
              <div style={{ marginTop: 8 }}>
                <AuditTimeline logs={history.filter((h) => String(h?.entity_type || '').toLowerCase() === 'meeting')} showEntity={false} maxCollapsedChanges={3} />
              </div>
            </details>
          </div>
        </div>
      )}

      {activeTab === 'job-openings' && (
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>{t('jobs.title', 'Job Openings')}</h3>
            {canCreateJobs && (
              <button type="button" className="btn btn-primary btn-sm" onClick={openCreateJobModal}>
                <FiPlus /> {t('jobs.addBtn', 'Add Job Opening')}
              </button>
            )}
          </div>
          {jobOpenings.length === 0 ? (
            <div className="empty-state"><p>{t('jobs.noJobs', 'No job openings found')}</p></div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>{t('jobs.offerTitle', 'Offer Title')}</th>
                    <th>{t('settings.fields.programType', 'Program Type')}</th>
                    <th>{t('jobs.jobType', 'Job Type')}</th>
                    <th>{t('jobs.contractType', 'Contract Type')}</th>
                    <th>{t('jobs.assignedManager', 'Assigned Manager')}</th>
                    <th>{t('jobs.nextFollowup', 'Next Follow-up')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {jobOpenings.map((job) => (
                    <tr key={job.job_id}>
                      <td>
                        {job.job_id ? (
                          <button
                            type="button"
                            className="btn-link"
                            style={{ padding: 0, border: 0, background: 'none', color: 'var(--primary)', cursor: 'pointer', fontWeight: 600 }}
                            onClick={() => navigate(`/jobs/${job.job_id}`)}
                          >
                            {job.offer_title || job.job_title || '-'}
                          </button>
                        ) : (job.offer_title || job.job_title || '-')}
                      </td>
                      <td>{job.program_type || 'All'}</td>
                      <td>{job.job_type || '-'}</td>
                      <td>{job.contract_type || '-'}</td>
                      <td>{job.assigned_manager_name || '-'}</td>
                      <td>{job.next_followup_date || '-'}</td>
                      <td>
                        <div className="table-actions">
                          {canUpdateJobs && (
                            <button className="edit" title={t('common.edit')} onClick={() => openEditJobModal(job)}>
                              <FiEdit />
                            </button>
                          )}
                          {canDeleteJobs && (
                            <button className="delete" title={t('common.delete')} onClick={() => handleDeleteJobOpening(job.job_id)}>
                              <FiTrash2 />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {activeTab === 'documents' && (
        <div className="card" style={{ padding: 18 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
            <h3 style={{ margin: 0 }}>{t('studentDetail.tabs.documents', 'Documents')}</h3>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => setShowUploadDocumentModal(true)}>
              <FiUpload /> {t('studentDetail.uploadDocument', 'Upload Document')}
            </button>
          </div>
          {documents.length === 0 ? (
            <div className="empty-state"><p>{t('studentDetail.documents.noDocuments', 'No documents found')}</p></div>
          ) : (
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>{t('studentDetail.documents.documentType', 'Document Type')}</th>
                    <th>{t('studentDetail.documents.fileName', 'File Name')}</th>
                    <th>{t('studentDetail.documents.uploadedDate', 'Uploaded Date')}</th>
                    <th>{t('studentDetail.documents.uploadedBy', 'Uploaded By')}</th>
                    <th>{t('common.notes', 'Notes')}</th>
                    <th>{t('common.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => (
                    <tr key={doc.company_document_id}>
                      <td>{doc.document_type || '-'}</td>
                      <td>{doc.file_name || '-'}</td>
                      <td>{doc.uploaded_at ? new Date(doc.uploaded_at).toLocaleDateString('en-IN', { timeZone: 'Asia/Kolkata' }) : '-'}</td>
                      <td>{doc.uploaded_by_name || '-'}</td>
                      <td>{doc.notes || '-'}</td>
                      <td>
                        <div className="table-actions">
                          <button className="view" title={t('common.view')} onClick={() => handleViewCompanyDocument(doc.company_document_id)}><FiEye /></button>
                          <button className="view" title={t('common.view')} onClick={() => companyDocumentsAPI.download(doc.company_document_id)}><FiDownload /></button>
                          <button className="delete" title={t('common.delete')} onClick={() => handleDeleteCompanyDocument(doc.company_document_id)}><FiTrash2 /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div style={{ marginTop: 14 }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                {t('studentDetail.historyTitles.documents', 'Documents History')}
              </summary>
              <div style={{ marginTop: 8 }}>
                <AuditTimeline
                  logs={history.filter((h) => String(h?.entity_type || '').toLowerCase() === 'document')}
                  showEntity={false}
                  maxCollapsedChanges={3}
                />
              </div>
            </details>
          </div>
        </div>
      )}

      {activeTab === 'history' && (
        <div className="card" style={{ padding: 18 }}>
          <h3 style={{ marginTop: 0 }}>{t('studentDetail.tabs.history', 'History')}</h3>
          {history.length === 0 ? (
            <div className="empty-state"><p>{t('common.noData', 'No data found')}</p></div>
          ) : (
            <AuditTimeline logs={history} showEntity={false} maxCollapsedChanges={3} />
          )}
        </div>
      )}

      {activeTab === 'emails' && (
        <div>
          <EmailThreadPanel
            entityType="company"
            entityId={id}
            entityEmail={company?.company_email || company?.email || company?.contact_email || ''}
          />
          <div style={{ marginTop: 14 }}>
            <details>
              <summary style={{ cursor: 'pointer', fontWeight: 700, fontSize: 13 }}>
                {t('studentDetail.historyTitles.emails', 'Emails History')}
              </summary>
              <div style={{ marginTop: 8 }}>
                <AuditTimeline
                  logs={history.filter((h) => String(h?.action || '').toLowerCase().startsWith('email_'))}
                  showEntity={false}
                  maxCollapsedChanges={3}
                />
              </div>
            </details>
          </div>
        </div>
      )}

      <Modal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        title={t('companies.editTitle', 'Edit Company')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowEditModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleUpdateCompany}>{t('common.update')}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.generalInformation')}</h4>
          </div>
          <div className="form-group full-width">
            <label>{t('companies.companyName')} <span className="required">*</span></label>
            <input value={editForm.company_name} onChange={(e) => setEditForm((f) => ({ ...f, company_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.siret')}</label>
            <input value={editForm.siret_number} onChange={(e) => setEditForm((f) => ({ ...f, siret_number: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('companies.companyAddress')}</label>
            <textarea rows={3} value={editForm.company_address} onChange={(e) => setEditForm((f) => ({ ...f, company_address: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.companyPhone')}</label>
            <input value={editForm.company_phone} onChange={(e) => setEditForm((f) => ({ ...f, company_phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.companyEmail')}</label>
            <input type="email" value={editForm.company_email} onChange={(e) => setEditForm((f) => ({ ...f, company_email: e.target.value }))} />
          </div>

          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.mainContact')}</h4>
          </div>
          <div className="form-group">
            <label>{t('companies.contactFirstName')}</label>
            <input value={editForm.contact_first_name} onChange={(e) => setEditForm((f) => ({ ...f, contact_first_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactLastName')}</label>
            <input value={editForm.contact_last_name} onChange={(e) => setEditForm((f) => ({ ...f, contact_last_name: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactPosition')}</label>
            <input value={editForm.contact_position} onChange={(e) => setEditForm((f) => ({ ...f, contact_position: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactPhone')}</label>
            <input value={editForm.contact_phone} onChange={(e) => setEditForm((f) => ({ ...f, contact_phone: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('companies.contactEmail')}</label>
            <input type="email" value={editForm.contact_email} onChange={(e) => setEditForm((f) => ({ ...f, contact_email: e.target.value }))} />
          </div>

          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.relationshipManager')}</h4>
          </div>
          <div className="form-group full-width">
            <label>{t('companies.relationshipManagerName')}</label>
            <select value={editForm.relationship_manager_id} onChange={(e) => setEditForm((f) => ({ ...f, relationship_manager_id: e.target.value }))}>
              <option value="">{t('companies.noneAssigned', 'None')}</option>
              {employees.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>

          <div className="form-group full-width">
            <h4 style={{ margin: 0 }}>{t('companies.companyStatus')}</h4>
          </div>
          <div className="form-group">
            <label>{t('companies.prospectTemperature')}</label>
            <select value={editForm.prospect_temperature} onChange={(e) => setEditForm((f) => ({ ...f, prospect_temperature: e.target.value }))}>
              {PROSPECT_TEMPERATURE_OPTIONS.map((opt) => <option key={opt} value={opt}>{prospectTemperatureLabel(opt)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.partnerStatus')}</label>
            <select value={editForm.partner_status} onChange={(e) => setEditForm((f) => ({ ...f, partner_status: e.target.value }))}>
              {PARTNER_STATUS_OPTIONS.map((opt) => <option key={opt} value={opt}>{partnerStatusLabel(opt)}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.hasOpenPosition')}</label>
            <select value={editForm.has_open_position ? '1' : '0'} onChange={(e) => setEditForm((f) => ({ ...f, has_open_position: e.target.value === '1' }))}>
              <option value="0">{t('common.no')}</option>
              <option value="1">{t('common.yes')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('companies.relationshipStage')}</label>
            <select value={editForm.relationship_stage} onChange={(e) => setEditForm((f) => ({ ...f, relationship_stage: e.target.value }))}>
              <option value="">{t('common.select')}</option>
              {RELATIONSHIP_STAGE_OPTIONS.map((opt) => <option key={opt} value={opt}>{relationshipStageLabel(opt)}</option>)}
            </select>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showActivityModal}
        onClose={() => setShowActivityModal(false)}
        title={t('leadDetail.modals.logActivity', 'Log Activity')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowActivityModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleCreateActivity}>{t('common.save')}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('activities.type', 'Type')}</label>
            <select value={activityForm.activity_type} onChange={(e) => setActivityForm((f) => ({ ...f, activity_type: e.target.value }))}>
              {['Call', 'Email', 'WhatsApp', 'Meeting', 'Follow-up'].map((type) => (
                <option key={type} value={type}>{t(`companies.meetingType.${type}`, { defaultValue: type })}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('activities.date', 'Date')}</label>
            <input type="date" value={activityForm.activity_date} onChange={(e) => setActivityForm((f) => ({ ...f, activity_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('activities.time', 'Time')}</label>
            <input type="time" value={activityForm.activity_time} onChange={(e) => setActivityForm((f) => ({ ...f, activity_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('activities.nextFollowup', 'Next Follow-up')}</label>
            <input type="date" value={activityForm.next_followup} onChange={(e) => setActivityForm((f) => ({ ...f, next_followup: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('activities.notes', 'Notes')}</label>
            <textarea rows={4} value={activityForm.notes} onChange={(e) => setActivityForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showUploadDocumentModal}
        onClose={() => setShowUploadDocumentModal(false)}
        title={t('studentDetail.modals.uploadDocument', 'Upload Document')}
        footer={(
          <>
            <button className="btn btn-outline" disabled={uploadingDocuments} onClick={() => setShowUploadDocumentModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" disabled={uploadingDocuments} onClick={handleUploadCompanyDocuments}>
              {uploadingDocuments ? t('common.uploading', 'Uploading...') : t('common.upload', 'Upload')}
            </button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group">
            <label>{t('studentDetail.documents.documentType', 'Document Type')}</label>
            <input value={documentForm.document_type} onChange={(e) => setDocumentForm((f) => ({ ...f, document_type: e.target.value }))} placeholder="General" />
          </div>
          <div className="form-group full-width">
            <label>{t('studentDetail.documents.selectFile', 'Select File')} <span className="required">*</span></label>
            <input
              type="file"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt"
              onChange={(e) => setDocumentForm((f) => ({ ...f, files: Array.from(e.target.files || []) }))}
            />
            {documentForm.files.length > 0 && (
              <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)' }}>
                {documentForm.files.length} file(s) selected: {documentForm.files.map((f) => f.name).join(', ')}
              </div>
            )}
          </div>
          <div className="form-group full-width">
            <label>{t('common.notes', 'Notes')}</label>
            <textarea rows={3} value={documentForm.notes} onChange={(e) => setDocumentForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showJobModal}
        onClose={() => setShowJobModal(false)}
        title={editingJob ? t('jobs.editTitle', 'Edit Job Opening') : t('jobs.addTitle', 'Add Job Opening')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowJobModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleSaveJobOpening}>
              {editingJob ? t('common.update') : t('common.create')}
            </button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('jobs.offerTitle', 'Offer Title')} <span className="required">*</span></label>
            <input value={jobForm.offer_title} onChange={(e) => setJobForm((f) => ({ ...f, offer_title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.positions', 'Number of Positions')}</label>
            <input
              type="number"
              min="1"
              value={jobForm.number_of_positions}
              onChange={(e) => setJobForm((f) => ({ ...f, number_of_positions: e.target.value }))}
            />
          </div>
          <div className="form-group">
            <label>{t('settings.fields.programType', 'Program Type')}</label>
            <select value={jobForm.program_type} onChange={(e) => setJobForm((f) => ({ ...f, program_type: e.target.value }))}>
              <option value="All">{t('common.all', 'All')}</option>
              {programTypes.map((p) => (
                <option key={p.program_type_id || p.program_type_name} value={p.program_type_name}>
                  {p.program_type_name}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.jobType', 'Job Type')}</label>
            <select value={jobForm.job_type} onChange={(e) => setJobForm((f) => ({ ...f, job_type: e.target.value }))}>
              {JOB_TYPES.map((opt) => <option key={opt} value={opt}>{t(`jobs.type.${opt}`, { defaultValue: opt })}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.contractType', 'Contract Type')}</label>
            <select value={jobForm.contract_type} onChange={(e) => setJobForm((f) => ({ ...f, contract_type: e.target.value }))}>
              {CONTRACT_TYPES.map((opt) => <option key={opt} value={opt}>{t(`jobs.contractType.${opt}`, { defaultValue: opt })}</option>)}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.responsibilities', 'Responsibilities')}</label>
            <textarea value={jobForm.responsibilities} onChange={(e) => setJobForm((f) => ({ ...f, responsibilities: e.target.value }))} rows={3} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.exchangeHistory', 'Exchange History')}</label>
            <textarea value={jobForm.exchange_history} onChange={(e) => setJobForm((f) => ({ ...f, exchange_history: e.target.value }))} rows={3} />
          </div>
          <div className="form-group">
            <label>{t('jobs.lastContact', 'Last Contact')}</label>
            <input type="date" value={jobForm.last_contact_date} onChange={(e) => setJobForm((f) => ({ ...f, last_contact_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('jobs.nextFollowup', 'Next Follow-up')}</label>
            <input type="date" value={jobForm.next_followup_date} onChange={(e) => setJobForm((f) => ({ ...f, next_followup_date: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.followupAction', 'Follow-up Action')}</label>
            <textarea value={jobForm.followup_action} onChange={(e) => setJobForm((f) => ({ ...f, followup_action: e.target.value }))} rows={2} />
          </div>
          <div className="form-group">
            <label>{t('jobs.emailStage', 'Email Stage')}</label>
            <input value={jobForm.email_stage} onChange={(e) => setJobForm((f) => ({ ...f, email_stage: e.target.value }))} placeholder={t('jobs.emailStagePlaceholder')} />
          </div>
          <div className="form-group">
            <label>{t('jobs.reminderEnabled', 'Reminder Enabled')}</label>
            <select value={jobForm.reminder_enabled ? '1' : '0'} onChange={(e) => setJobForm((f) => ({ ...f, reminder_enabled: e.target.value === '1' }))}>
              <option value="0">{t('common.no')}</option>
              <option value="1">{t('common.yes')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.assignedManager', 'Assigned Manager')}</label>
            <select value={jobForm.assigned_manager_id} onChange={(e) => setJobForm((f) => ({ ...f, assigned_manager_id: e.target.value }))}>
              <option value="">{t('jobs.noneCompany')}</option>
              {employees.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label>{t('jobs.handoverManager', 'Handover Manager')}</label>
            <select value={jobForm.handover_manager_id} onChange={(e) => setJobForm((f) => ({ ...f, handover_manager_id: e.target.value }))}>
              <option value="">{t('jobs.noneCompany')}</option>
              {employees.map((u) => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
            </select>
          </div>
          <div className="form-group full-width">
            <label>{t('jobs.meetingHistory', 'Meeting History')}</label>
            <textarea value={jobForm.meeting_history} onChange={(e) => setJobForm((f) => ({ ...f, meeting_history: e.target.value }))} rows={2} />
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={showMeetingModal}
        onClose={() => setShowMeetingModal(false)}
        title={t('leadDetail.scheduleMeeting', 'Schedule Meeting')}
        footer={(
          <>
            <button className="btn btn-outline" onClick={() => setShowMeetingModal(false)}>{t('common.cancel')}</button>
            <button className="btn btn-primary" onClick={handleCreateMeeting}>{t('common.save')}</button>
          </>
        )}
      >
        <div className="form-grid">
          <div className="form-group full-width">
            <label>{t('meetings.title', 'Title')}</label>
            <input value={meetingForm.title} onChange={(e) => setMeetingForm((f) => ({ ...f, title: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('meetings.type', 'Type')}</label>
            <input value={meetingForm.meeting_type} onChange={(e) => setMeetingForm((f) => ({ ...f, meeting_type: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('meetings.mode', 'Mode')}</label>
            <select value={meetingForm.mode} onChange={(e) => setMeetingForm((f) => ({ ...f, mode: e.target.value }))}>
              <option value="Online">{t('common.online', 'Online')}</option>
              <option value="Offline">{t('common.offline', 'Offline')}</option>
            </select>
          </div>
          <div className="form-group">
            <label>{t('meetings.date', 'Date')}</label>
            <input type="date" value={meetingForm.meeting_date} onChange={(e) => setMeetingForm((f) => ({ ...f, meeting_date: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('meetings.time', 'Time')}</label>
            <input type="time" value={meetingForm.meeting_time} onChange={(e) => setMeetingForm((f) => ({ ...f, meeting_time: e.target.value }))} />
          </div>
          <div className="form-group">
            <label>{t('meetings.timezone', 'Timezone')}</label>
            <input value={meetingForm.timezone} onChange={(e) => setMeetingForm((f) => ({ ...f, timezone: e.target.value }))} />
          </div>
          <div className="form-group full-width">
            <label>{meetingForm.mode === 'Online' ? t('common.link', 'Link') : t('common.location', 'Location')}</label>
            {meetingForm.mode === 'Online' ? (
              <input value={meetingForm.meeting_link} onChange={(e) => setMeetingForm((f) => ({ ...f, meeting_link: e.target.value }))} />
            ) : (
              <input value={meetingForm.location} onChange={(e) => setMeetingForm((f) => ({ ...f, location: e.target.value }))} />
            )}
          </div>
          {meetingForm.mode === 'Online' && zoomConfigured && (
            <div className="form-group full-width" style={{ marginTop: -4 }}>
              <button type="button" className="btn btn-sm btn-outline" disabled={zoomGeneratingModal} onClick={handleGenerateZoomForMeetingModal}>
                <FiVideo size={14} /> {zoomGeneratingModal ? t('leadDetail.creatingZoom', 'Creating Zoom...') : t('leadDetail.generateZoom', 'Generate Zoom')}
              </button>
              {meetingForm.zoom_meeting_id && (
                <div style={{ marginTop: 6, fontSize: 12, color: 'var(--text-muted)' }}>
                  Zoom ID: {meetingForm.zoom_meeting_id}
                </div>
              )}
            </div>
          )}
          <div className="form-group full-width">
            <label>{t('activities.notes', 'Notes')}</label>
            <textarea rows={3} value={meetingForm.notes} onChange={(e) => setMeetingForm((f) => ({ ...f, notes: e.target.value }))} />
          </div>
          {editingMeeting && (
            <div className="form-group">
              <label>{t('leadDetail.meetingStatus', 'Meeting Status')}</label>
              <select value={meetingForm.status || 'Scheduled'} onChange={(e) => setMeetingForm((f) => ({ ...f, status: e.target.value }))}>
                {['Scheduled', 'Completed', 'Cancelled'].map((s) => <option key={s} value={s}>{t(`common.meetingStatus.${s}`, { defaultValue: s })}</option>)}
              </select>
              <p style={{ fontSize: 12, color: 'var(--text-muted)', margin: '6px 0 0' }}>
                To reschedule with new date/time, set status to <strong>Rescheduled</strong> from the meeting card.
              </p>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showRescheduleModal && !!rescheduleForm && !!rescheduleMeeting}
        onClose={closeRescheduleModal}
        title={t('leadDetail.rescheduleTitle', 'Reschedule Meeting')}
        size="lg"
        footer={(
          <>
            <button type="button" className="btn btn-outline" onClick={closeRescheduleModal}>{t('common.cancel')}</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleRescheduleSubmit}
              disabled={updatingMeetingStatusId === rescheduleMeeting?.meeting_id}
            >
              {updatingMeetingStatusId === rescheduleMeeting?.meeting_id ? t('common.saving', 'Saving...') : t('leadDetail.rescheduleTitle', 'Reschedule Meeting')}
            </button>
          </>
        )}
      >
        {rescheduleMeeting && rescheduleForm && (
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Meeting</label>
              <p style={{ margin: 0, fontWeight: 600 }}>{rescheduleMeeting.title}</p>
            </div>
            <div className="form-group">
              <label>{t('leadDetail.newDate', 'New Date')} <span className="required">*</span></label>
              <input type="date" value={rescheduleForm.meeting_date} onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_date: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t('leadDetail.newTime', 'New Time')} <span className="required">*</span></label>
              <input type="time" value={rescheduleForm.meeting_time} onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_time: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t('leadDetail.meetingTimezone', 'Timezone')}</label>
              <input value={rescheduleForm.timezone} onChange={(e) => setRescheduleForm((f) => ({ ...f, timezone: e.target.value }))} />
            </div>
            <div className="form-group">
              <label>{t('leadDetail.meetingMode', 'Mode')}</label>
              <select value={rescheduleForm.mode} onChange={(e) => setRescheduleForm((f) => ({ ...f, mode: e.target.value }))}>
                <option value="Online">{t('common.online', 'Online')}</option>
                <option value="Offline">{t('common.offline', 'Offline')}</option>
              </select>
            </div>
            {rescheduleForm.mode === 'Offline' && (
              <div className="form-group full-width">
                <label>{t('leadDetail.meetingLocation', 'Location')} <span className="required">*</span></label>
                <input value={rescheduleForm.location} onChange={(e) => setRescheduleForm((f) => ({ ...f, location: e.target.value }))} />
              </div>
            )}
            {rescheduleForm.mode === 'Online' && (
              <div className="form-group full-width">
                <label>{t('leadDetail.meetingLink', 'Meeting Link')}</label>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <input style={{ flex: 1, minWidth: 200 }} value={rescheduleForm.meeting_link} onChange={(e) => setRescheduleForm((f) => ({ ...f, meeting_link: e.target.value }))} />
                  {zoomConfigured && (
                    <button
                      type="button"
                      className="btn btn-outline btn-sm"
                      style={{ whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      disabled={zoomGeneratingReschedule}
                      onClick={generateZoomInRescheduleModal}
                    >
                      <FiVideo size={14} /> {zoomGeneratingReschedule ? t('leadDetail.working', 'Working...') : t('leadDetail.generateZoom', 'Generate Zoom')}
                    </button>
                  )}
                </div>
                {rescheduleForm.zoom_meeting_id && (
                  <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 6 }}>
                    Zoom meeting ID: {rescheduleForm.zoom_meeting_id}
                    {rescheduleForm.zoom_passcode ? ` · Passcode: ${rescheduleForm.zoom_passcode}` : ''}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CompanyDetail;
